/**
 * Record pages: a record opened from its management grid, with one routed tab
 * per kind of thing that belongs to it (decision 0072).
 *
 * Declared here as data because the route tree is JSX and a test cannot read
 * it. The shell reads this table to draw the path bar and the record's tabs;
 * `recordPages.test.ts` holds the one-level rule over it.
 */

import { PAGE_ROUTES, type PageKey } from './pageRegistry';

export interface RecordTab {
  /** The tab's last address segment, below the record's address. */
  segment: string;
  labelKey: string;
  /**
   * The grant that offers the tab. A tab showing another page's records needs
   * that page's grant, not only the list's.
   */
  page: PageKey;
  /** A tenant action the tab also needs, held in the tenant the app is showing. */
  action?: string;
}

export interface RecordPage {
  /** The page key of the grid the record is opened from; it governs the record page. */
  list: PageKey;
  /** The record's address, its ids as `:param` segments. */
  path: string;
  tabs: readonly RecordTab[];
}

export const RECORD_PAGES = {
  programme: {
    list: 'manage-programmes',
    path: `${PAGE_ROUTES['manage-programmes']}/:programmeId`,
    tabs: [
      { segment: 'general', labelKey: 'recordPage.general', page: 'manage-programmes' },
      { segment: 'measurements', labelKey: 'programmePage.measurements', page: 'manage-measurements' },
      { segment: 'dashboards', labelKey: 'programmePage.dashboards', page: 'manage-programmes' },
      {
        segment: 'people',
        labelKey: 'programmePage.people',
        page: 'manage-programmes',
        action: 'programme:enrollment:view',
      },
    ],
  },
  organisation: {
    list: 'admin-organisations',
    path: `${PAGE_ROUTES['admin-organisations']}/:organisationId`,
    tabs: [
      { segment: 'general', labelKey: 'recordPage.general', page: 'admin-organisations' },
      { segment: 'people', labelKey: 'organisationPage.people', page: 'admin-organisations' },
    ],
  },
  tenant: {
    list: 'admin-tenants',
    path: `${PAGE_ROUTES['admin-tenants']}/:tenantId`,
    tabs: [
      { segment: 'general', labelKey: 'recordPage.general', page: 'admin-tenants' },
      { segment: 'administrators', labelKey: 'tenantPage.administrators', page: 'admin-tenants' },
    ],
  },
  questionnaire: {
    list: 'admin-questionnaires',
    path: `${PAGE_ROUTES['admin-questionnaires']}/:questionnaireId`,
    tabs: [
      { segment: 'questions', labelKey: 'questionnairePage.questions', page: 'admin-questionnaires' },
      { segment: 'submissions', labelKey: 'questionnairePage.submissions', page: 'admin-questionnaires' },
    ],
  },
  dataProvider: {
    list: 'admin-data-providers',
    path: `${PAGE_ROUTES['admin-data-providers']}/:providerId`,
    tabs: [
      { segment: 'general', labelKey: 'recordPage.general', page: 'admin-data-providers' },
      { segment: 'offers', labelKey: 'dataProviderPage.offers', page: 'admin-data-providers' },
    ],
  },
  document: {
    list: 'documents',
    path: `${PAGE_ROUTES.documents}/:documentId`,
    tabs: [
      { segment: 'document', labelKey: 'documentPage.document', page: 'documents' },
    ],
  },
  dataSource: {
    list: 'data-providers',
    path: `${PAGE_ROUTES['data-providers']}/:providerId`,
    tabs: [{ segment: 'general', labelKey: 'recordPage.general', page: 'data-providers' }],
  },
  measurement: {
    list: 'manage-data',
    path: `${PAGE_ROUTES['manage-data']}/:measurementId`,
    tabs: [
      { segment: 'general', labelKey: 'recordPage.general', page: 'manage-data' },
      { segment: 'units', labelKey: 'masterData.measurementPage.units', page: 'manage-data' },
    ],
  },
} as const satisfies Record<string, RecordPage>;

export type RecordPageKey = keyof typeof RECORD_PAGES;

/**
 * Record pages allowed below another record page, each with the reason nothing
 * else can hold what it shows. Empty: every record page is one level deep.
 */
export const NESTED_RECORD_PAGES: Readonly<Partial<Record<RecordPageKey, string>>> = {};

export type RecordParams = Readonly<Record<string, string | number>>;

const PARAM = /:([A-Za-z]+)/g;

/** An address pattern with its params filled in. */
export const fillPath = (path: string, params: RecordParams): string =>
  path.replace(PARAM, (_whole, name: string) => {
    const value = params[name];
    if (value === undefined) throw new Error(`No ${name} for ${path}`);
    return encodeURIComponent(String(value));
  });

/** The params of an address pattern, and the one segment after it if any. */
export const matchPathname = (
  path: string,
  pathname: string,
): { params: Record<string, string>; segment: string | null } | null => {
  const names = [...path.matchAll(PARAM)].map((match) => match[1]);
  const pattern = path
    .split(PARAM)
    .map((part, index) => (index % 2 === 1 ? '([^/]+)' : part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')))
    .join('');
  const match = new RegExp(`^${pattern}(?:/([^/]+))?/?$`).exec(pathname);
  if (!match) return null;
  let params: Record<string, string>;
  try {
    params = Object.fromEntries(names.map((name, index) => [name, decodeURIComponent(match[index + 1])]));
  } catch (err) {
    // A malformed escape in a typed address matches nothing; it must not take the shell down.
    if (err instanceof URIError) return null;
    throw err;
  }
  return { params, segment: match[names.length + 1] ?? null };
};

/** A record's address, and a tab's when a segment is given. */
export const recordPath = (key: RecordPageKey, params: RecordParams, segment?: string): string => {
  const base = fillPath(RECORD_PAGES[key].path, params);
  return segment ? `${base}/${segment}` : base;
};

export interface RecordMatch {
  key: RecordPageKey;
  page: RecordPage;
  params: Record<string, string>;
  /** Null on the record's bare address, which opens its first held tab. */
  tab: RecordTab | null;
}

/** Which record page, and which of its tabs, an address is on. */
export const matchRecordPage = (pathname: string): RecordMatch | null => {
  for (const key of Object.keys(RECORD_PAGES) as RecordPageKey[]) {
    const page: RecordPage = RECORD_PAGES[key];
    const match = matchPathname(page.path, pathname);
    if (!match) continue;
    if (match.segment === null) return { key, page, params: match.params, tab: null };
    const tab = page.tabs.find((candidate) => candidate.segment === match.segment);
    if (tab) return { key, page, params: match.params, tab };
  }
  return null;
};

/** The tabs this reader is offered, in the table's order; a tab needing an action waits for `actions`. */
export const heldTabs = (
  page: RecordPage,
  allowedPages: ReadonlySet<PageKey>,
  actions: ReadonlySet<string> | null,
): RecordTab[] =>
  page.tabs.filter((tab) => allowedPages.has(tab.page) && (tab.action === undefined || !!actions?.has(tab.action)));
